import { observer } from "mobx-react-lite";
import { Button } from "primereact/button";
import { BreadCrumb } from "primereact/breadcrumb";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SmilesView from "../../../../app/common/SmilesView/SmilesView";
import SectionHeading from "../../../../app/common/SectionHeading/SectionHeading";
import SmilesDrawer from "smiles-drawer";
import { RootStoreContext } from "../../../../app/stores/rootStore";
import "./PatentCompounds.css";
import { appColors } from "../../../../colors";

const PatentCompounds = ({ id: projectId }) => {
  const navigate = useNavigate();
  const rootStore = useContext(RootStoreContext);
  const { selectedProject } = rootStore.projectStore;
  const { compounds, columns, loading, selectedGroupId, isUngrouped, fetchCompounds, fetchColumns, fetchGroups, saveCompound, deleteCompound, saveColumns, importCsv } =
    rootStore.patentCompoundStore;

  const toast = useRef(null);
  const [localRows, setLocalRows] = useState([]);
  const [displayColumnDialog, setDisplayColumnDialog] = useState(false);
  const [editingColumns, setEditingColumns] = useState([]);
  const [newColumnName, setNewColumnName] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const editSnapshot = useRef(null);
  const [structurePreview, setStructurePreview] = useState(null);
  const [rowHeight, setRowHeight] = useState(50);

  // ── Redraw all structure canvases ───────────────────────────────
  const redrawAllCanvases = useCallback(() => {
    document.querySelectorAll(".patent-structure-canvas").forEach((el) => {
      const smiles = el.dataset.smiles;
      if (!smiles) return;
      const parent = el.parentElement;
      const cellWidth = parent?.offsetWidth || 200;
      const cellHeight = parent?.offsetHeight || 60;
      const w = Math.max(cellWidth - 16, 60);
      const h = Math.max(cellHeight - 4, 36);
      const dpr = window.devicePixelRatio || 1;
      el.width = w * dpr;
      el.height = h * dpr;
      el.style.width = w + "px";
      el.style.height = h + "px";
      try {
        const drawer = new SmilesDrawer.Drawer({
          width: w * dpr, height: h * dpr,
          bondThickness: 1.0, shortBondLength: 1.0,
          fontSizeScale: 0.8, atomVisualization: "default",
          isomericHighlight: true, explicitHydrogens: false,
        });
        SmilesDrawer.parse(
          smiles,
          (tree) => drawer.draw(tree, el.id, "light", false),
          (err) => console.error("SmilesDrawer error:", err)
        );
      } catch (e) {
        console.error("SmilesDrawer render error:", e);
      }
    });
  }, []);

  // Initial redraw after data loads
  useEffect(() => {
    if (localRows.length > 0) {
      const t1 = setTimeout(() => redrawAllCanvases(), 100);
      const t2 = setTimeout(() => redrawAllCanvases(), 500);
      const t3 = setTimeout(() => redrawAllCanvases(), 1500);
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }
  }, [localRows, rowHeight, redrawAllCanvases]);

  // BreadCrumb
  const breadCrumbItems = [
    { label: "Project Management", command: () => navigate("/pm/") },
    { label: "Projects", command: () => navigate("/pm/project/") },
    { label: selectedProject?.projectName || "Project" },
    { label: "Patent Compounds" },
  ];

  // Load data
  useEffect(() => {
    if (projectId) {
      fetchGroups(projectId);
      fetchCompounds(projectId);
      fetchColumns(projectId);
    }
  }, [projectId, selectedGroupId, isUngrouped, fetchCompounds, fetchColumns, fetchGroups]);

  // Auto-select first group when groups load and nothing is selected
  const { groups, selectGroup } = rootStore.patentCompoundStore;
  useEffect(() => {
    if (groups.length > 0 && !selectedGroupId && !isUngrouped) {
      selectGroup(groups[0].id);
    }
  }, [groups]);

  // Sync local rows when compounds/columns change
  useEffect(() => {
    if (compounds && columns) {
      setLocalRows(
        compounds.map((c) => {
          let extra = {};
          try {
            extra = c.extraData ? JSON.parse(c.extraData) : {};
          } catch (_) {}
          return {
            id: c.id,
            projectId: c.projectId,
            compoundId: c.compoundId || "",
            smile: c.smile || "",
            extra, // { colName: value, ... }
            _isNew: false,
          };
        })
      );
    }
  }, [compounds, columns]);

  // ── Row editing ─────────────────────────────────────────────────
  const startEditing = (row, index) => {
    editSnapshot.current = JSON.parse(JSON.stringify(row));
    setEditingIndex(index);
  };

  const cancelEditing = () => {
    if (editSnapshot.current !== null && editingIndex !== null) {
      setLocalRows((prev) => {
        const updated = [...prev];
        updated[editingIndex] = editSnapshot.current;
        return updated;
      });
    }
    editSnapshot.current = null;
    setEditingIndex(null);
  };

  // ── Add empty row ───────────────────────────────────────────────
  const addEmptyRow = () => {
    editSnapshot.current = null;
    setEditingIndex(null);
    setLocalRows((prev) => [
      ...prev,
      {
        id: null,
        projectId,
        compoundId: "",
        smile: "",
        extra: {},
        _isNew: true,
      },
    ]);
  };

  // ── Update field ────────────────────────────────────────────────
  const updateField = (index, field, value) => {
    setLocalRows((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const updateExtraField = (index, colName, value) => {
    setLocalRows((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        extra: { ...updated[index].extra, [colName]: value },
      };
      return updated;
    });
  };

  // ── Save a single row ───────────────────────────────────────────
  const handleSaveRow = async (row, index) => {
    const extraJson = JSON.stringify(row.extra);
    const result = await saveCompound(projectId, {
      id: row.id,
      compoundId: row.compoundId,
      smile: row.smile,
      extraData: extraJson,
    });
    if (result !== null) {
      toast.current.show({ severity: "success", summary: "Saved", detail: "Row saved" });
      setEditingIndex(null);
      editSnapshot.current = null;
    } else {
      toast.current.show({ severity: "error", summary: "Error", detail: "Failed to save" });
    }
  };

  // ── Delete a row ────────────────────────────────────────────────
  const handleDeleteRow = async (row, index) => {
    if (row.id) {
      await deleteCompound(row.id, projectId);
    } else {
      setLocalRows((prev) => prev.filter((_, i) => i !== index));
    }
  };

  // ── Column management ───────────────────────────────────────────
  const openColumnDialog = () => {
    setEditingColumns(
      columns.map((c) => ({
        id: c.id,
        columnName: c.columnName,
        displayName: c.displayName,
        dataType: c.dataType || "string",
        sortOrder: c.sortOrder,
      }))
    );
    setNewColumnName("");
    setDisplayColumnDialog(true);
  };

  const addCustomColumn = () => {
    if (!newColumnName.trim()) return;
    const colName = newColumnName.trim().toLowerCase().replace(/\s+/g, "_");
    setEditingColumns((prev) => [
      ...prev,
      {
        id: null,
        columnName: colName,
        displayName: newColumnName.trim(),
        dataType: "string",
        sortOrder: prev.length,
      },
    ]);
    setNewColumnName("");
  };

  const removeCustomColumn = (index) => {
    setEditingColumns((prev) => prev.filter((_, i) => i !== index));
  };

  const saveColumnChanges = async () => {
    const result = await saveColumns(projectId, editingColumns);
    if (result !== null) {
      toast.current.show({ severity: "success", summary: "Columns saved" });
      setDisplayColumnDialog(false);
    } else {
      toast.current.show({ severity: "error", summary: "Failed to save columns" });
    }
  };

  // ── CSV Import ──────────────────────────────────────────────────
  const csvInputRef = useRef(null);
  const handleCsvFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = await importCsv(projectId, file);
    if (result !== null) {
      toast.current.show({ severity: "success", summary: "Imported", detail: `${result} rows imported` });
    } else {
      toast.current.show({ severity: "error", summary: "Import failed" });
    }
    e.target.value = "";
  };

  // ── Cell Templates ──────────────────────────────────────────────
  const compoundIdTemplate = (rowData, options) => {
    const idx = options.rowIndex;
    if (editingIndex === idx) {
      return (
        <InputText
          value={rowData.compoundId}
          onChange={(e) => updateField(idx, "compoundId", e.target.value)}
          placeholder="Required"
          style={{ width: "100%", minWidth: 140 }}
        />
      );
    }
    return <span>{rowData.compoundId || <span style={{ color: "#cbd5e1" }}>—</span>}</span>;
  };

  const smileTemplate = (rowData, options) => {
    const idx = options.rowIndex;
    if (editingIndex === idx) {
      return (
        <InputText
          value={rowData.smile}
          onChange={(e) => updateField(idx, "smile", e.target.value)}
          placeholder="Required"
          style={{ width: "100%", minWidth: 200, fontFamily: "monospace", fontSize: 12 }}
        />
      );
    }
    return (
      <code style={{
        fontSize: "11px", color: "#64748b", background: "#f1f5f9",
        padding: "2px 6px", borderRadius: 4, maxWidth: 200,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block",
        fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
      }}>
        {rowData.smile || <span style={{ color: "#cbd5e1" }}>—</span>}
      </code>
    );
  };

  const structureTemplate = (rowData, options) => {
    if (!rowData.smile) return <span style={{ color: "#cbd5e1" }}>—</span>;
    return (
      <div style={{ position: "relative", width: "100%", height: rowHeight, minHeight: rowHeight }}>
        <div style={{ position: "absolute", inset: 0, display: "flex", justifyContent: "center", alignItems: "center", cursor: "pointer" }}
          onClick={() => setStructurePreview({ smiles: rowData.smile, compoundId: rowData.compoundId })}>
          <canvas
            id={`patent-structure-${rowData.id || Math.random()}`}
            className="patent-structure-canvas"
            data-smiles={rowData.smile}
            style={{ display: "block" }}
          />
        </div>
        <div
          className="row-resize-handle"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const startY = e.clientY;
            const startH = rowHeight;
            const onMove = (ev) => {
              const newH = Math.max(50, Math.min(300, startH + ev.clientY - startY));
              setRowHeight(newH);
            };
            const onUp = () => {
              document.removeEventListener('mousemove', onMove);
              document.removeEventListener('mouseup', onUp);
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
          }}
        />
      </div>
    );
  };

  const customColTemplate = (colName, rowData, options) => {
    const idx = options.rowIndex;
    if (editingIndex === idx) {
      return (
        <InputText
          value={rowData.extra?.[colName] || ""}
          onChange={(e) => updateExtraField(idx, colName, e.target.value)}
          style={{ width: "100%", minWidth: 100 }}
        />
      );
    }
    return <span>{rowData.extra?.[colName] || <span style={{ color: "#cbd5e1" }}>—</span>}</span>;
  };

  const actionTemplate = (rowData, options) => {
    const idx = options.rowIndex;
    const isEditing = editingIndex === idx;
    return (
      <div className="flex gap-1">
        {isEditing ? (
          <>
            <Button
              icon="pi pi-check"
              className="p-button-rounded p-button-success p-button-text"
              tooltip="Save"
              onClick={() => handleSaveRow(rowData, idx)}
            />
            <Button
              icon="pi pi-trash"
              className="p-button-rounded p-button-danger p-button-text"
              tooltip="Delete"
              onClick={() => handleDeleteRow(rowData, idx)}
            />
            <Button
              icon="pi pi-times"
              className="p-button-rounded p-button-secondary p-button-text"
              tooltip="Cancel"
              onClick={cancelEditing}
            />
          </>
        ) : (
          <Button
            icon="pi pi-pencil"
            className="p-button-rounded p-button-info p-button-text"
            tooltip="Modify"
            onClick={() => startEditing(rowData, idx)}
          />
        )}
      </div>
    );
  };

  // ── Build dynamic columns ───────────────────────────────────────
  const dynamicColumns = (columns || []).map((col) => (
    <Column
      key={col.columnName}
      field={col.columnName}
      header={col.displayName || col.columnName}
      body={(rowData, options) => customColTemplate(col.columnName, rowData, options)}
      sortable
      style={{ minWidth: 120, width: 120 }}
    />
  ));

  return (
    <div className="flex flex-column w-full fadein animation-duration-500" style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
      <Toast ref={toast} />

      <div className="flex w-full pb-2">
        <BreadCrumb model={breadCrumbItems} />
      </div>

      <div className="flex w-full align-items-center mb-1">
        <SectionHeading
          icon="icon icon-common icon-copy"
          heading="Patent Compounds"
          color={appColors.sectionHeadingBg.project}
          customButtons={[
            { label: "Add Row", icon: "pi pi-plus", action: addEmptyRow, className: "p-button-sm add-compound-btn" },
            { label: "Manage Columns", icon: "pi pi-cog", action: openColumnDialog, className: "p-button-sm add-compound-btn" },
            { label: "Import CSV", icon: "pi pi-upload", action: () => csvInputRef.current?.click(), className: "p-button-sm add-compound-btn" },
          ]}
        />
        {/* Hidden file input for CSV import */}
        <input type="file" accept=".csv" ref={csvInputRef} onChange={handleCsvFile} style={{ display: "none" }} />
      </div>

      {/* Data Table */}
      <div className="flex w-full" style={{ flex: 1, minHeight: 0, minWidth: 0, overflow: "auto" }}>
        <DataTable
          value={localRows}
          loading={loading}
          emptyMessage="No patent compounds. Click 'Add Row' to add one, or 'Import CSV' to bulk upload."
          responsiveLayout="scroll"
          size="small"
          scrollable
          scrollHeight="flex"
          resizableColumns
          columnResizeMode="expand"
          paginator
          rows={20}
          rowsPerPageOptions={[20, 50, 100]}
        >
          <Column
            field="compoundId"
            header="ID*"
            body={compoundIdTemplate}
            sortable
            style={{ minWidth: 50, width: 50, maxWidth: 50 }}
          />
          <Column
            field="smile"
            header="SMILES*"
            body={smileTemplate}
            sortable
            style={{ minWidth: 150, width: 150, maxWidth: 150 }}
          />
          <Column
            header="Structure"
            body={structureTemplate}
            style={{ minWidth: 90, width: 110 }}
          />
          {dynamicColumns}
          <Column
            header="Actions"
            body={actionTemplate}
            style={{ minWidth: 100, width: 100, maxWidth: 100 }}
          />
        </DataTable>
      </div>

      {/* Column Management Dialog */}
      <Dialog
        header="Manage Columns"
        visible={displayColumnDialog}
        onHide={() => setDisplayColumnDialog(false)}
        style={{ width: 500 }}
        footer={
          <div className="flex gap-2 justify-content-end">
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setDisplayColumnDialog(false)} />
            <Button label="Save Columns" icon="pi pi-check" onClick={saveColumnChanges} />
          </div>
        }
      >
        <div className="flex flex-column gap-3">
          {/* Existing columns */}
          {editingColumns.map((col, i) => (
            <div key={i} className="flex align-items-center gap-2">
              <InputText value={col.displayName} onChange={(e) => {
                const updated = [...editingColumns];
                updated[i] = { ...updated[i], displayName: e.target.value };
                setEditingColumns(updated);
              }} placeholder="Display name" style={{ flex: 1 }} />
              <Dropdown value={col.dataType} options={[
                { label: "Text", value: "string" },
                { label: "Number", value: "number" },
                { label: "Date", value: "date" },
              ]} onChange={(e) => {
                const updated = [...editingColumns];
                updated[i] = { ...updated[i], dataType: e.value };
                setEditingColumns(updated);
              }} style={{ width: 120 }} />
              <Button icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-text" onClick={() => removeCustomColumn(i)} />
            </div>
          ))}

          {/* Add new column */}
          <div className="flex align-items-center gap-2 pt-2" style={{ borderTop: "1px solid #e2e8f0" }}>
            <InputText
              value={newColumnName}
              onChange={(e) => setNewColumnName(e.target.value)}
              placeholder="New column name"
              style={{ flex: 1 }}
              onKeyDown={(e) => e.key === "Enter" && addCustomColumn()}
            />
            <Button label="Add" icon="pi pi-plus" className="p-button-sm" onClick={addCustomColumn} />
          </div>
        </div>
      </Dialog>

      {/* Structure Preview Dialog */}
      <Dialog
        header={structurePreview?.compoundId || "Structure"}
        visible={!!structurePreview}
        onHide={() => setStructurePreview(null)}
        style={{ width: 400 }}
        dismissableMask
      >
        {structurePreview && (
          <div className="flex justify-content-center p-3">
            <SmilesView smiles={structurePreview.smiles} width={350} height={280} />
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default observer(PatentCompounds);
